import { redirect } from 'react-router';
import type { AnalyticsReportingMode, Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import {
  ANALYTICS_REPORTING_MODE_SEARCH_PARAM,
  isGenie2ReportingMode,
  parseGenie2ReportingModeSearchParam,
  usesAnalyticsStatsUi,
} from '~/utils/analyticsReportingMode';

function toUrl(requestUrl: string): URL {
  return new URL(requestUrl, 'http://localhost');
}

function pathWithSearch(url: URL): string {
  return `${url.pathname}${url.search}`;
}

/** Set or replace the Genie 2.0 pre-seed search param; returns pathname + search. */
export function pathWithAnalyticsReportingModeSearch(
  requestUrl: string,
  mode: Genie2ReportingMode,
): string {
  const url = toUrl(requestUrl);
  url.searchParams.set(ANALYTICS_REPORTING_MODE_SEARCH_PARAM, mode);
  return pathWithSearch(url);
}

/** Strip the Genie 2.0 pre-seed search param; returns pathname + search. */
export function pathWithoutAnalyticsReportingModeSearch(requestUrl: string): string {
  const url = toUrl(requestUrl);
  url.searchParams.delete(ANALYTICS_REPORTING_MODE_SEARCH_PARAM);
  return pathWithSearch(url);
}

/**
 * Decide a replace redirect for analyticsReportingMode search sync:
 * - Live Genie 2.0 + param present → strip param
 * - Classic Genie + no param + parentGenie2Mode → seed parent's universe
 * Returns null when no redirect is needed.
 */
export function resolveAnalyticsReportingModeSearchRedirect({
  requestUrl,
  persistedMode,
  parentGenie2Mode = null,
}: {
  requestUrl: string;
  persistedMode: AnalyticsReportingMode | null | undefined;
  parentGenie2Mode?: Genie2ReportingMode | null;
}): string | null {
  if (persistedMode == null) {
    return null;
  }

  const url = toUrl(requestUrl);
  const hasParam = url.searchParams.has(ANALYTICS_REPORTING_MODE_SEARCH_PARAM);

  if (usesAnalyticsStatsUi(persistedMode) && hasParam) {
    return pathWithoutAnalyticsReportingModeSearch(requestUrl);
  }

  if (persistedMode === 'legacy' && parentGenie2Mode != null) {
    const searchMode = parseGenie2ReportingModeSearchParam(
      url.searchParams.get(ANALYTICS_REPORTING_MODE_SEARCH_PARAM),
    );
    if (searchMode == null && isGenie2ReportingMode(parentGenie2Mode)) {
      return pathWithAnalyticsReportingModeSearch(requestUrl, parentGenie2Mode);
    }
  }

  return null;
}

/** Throw a replace redirect when search should sync to persisted / parent mode. */
export function throwRedirectForAnalyticsReportingModeSearch(args: {
  requestUrl: string;
  persistedMode: AnalyticsReportingMode | null | undefined;
  parentGenie2Mode?: Genie2ReportingMode | null;
}): void {
  const to = resolveAnalyticsReportingModeSearchRedirect(args);
  if (to != null) {
    throw redirect(to);
  }
}
